import z from 'zod'
import { aspectRatioSchema } from '../database/custom-types/aspect-ratio'
import { playbackModeCoercedSchema } from '../database/custom-types/playback-mode'
import { normalizeFrameContinuityFlags, stripPlayContinuousFromFrames } from '../helpers/normalizeFrameContinuityFlags'
import { validateFrameWidgetUniqueness } from '../helpers/validateFrameWidgetUniqueness'
import { AssetType } from './asset-type'
import { ctaPlacementSchema } from './cta'
import { rssDirectoryRefSchema } from './rssDirectoryRef'
import { playlistAssignmentGeoFilterSchema } from './playlist-assignment-geo-filter'
import { timeBlockSchema } from './time-block'

/** Reusable schema for widget config (key-value). Use for campaign/playlist/facet validation. */
export const widgetConfigSchema = z.record(z.string(), z.unknown()).optional().nullable()

export const facetSchema = z.object({
  asset: z.string(),
  type: z.nativeEnum(AssetType),
  unmuted: z.boolean().optional(),
  /** Per-frame/section overrides for widget config (when type === 'widget'). Merged with campaign/playlist defaults by the player. */
  widgetConfig: z.record(z.string(), z.unknown()).optional(),
  /**
   * Identifies which singleton instance this facet connects to.
   *  If omitted, the singleton is keyed by URN alone (one instance per widget).
   *  When set, the key becomes `${urn}#${singletonId}`, allowing multiple persistent iframes of the same widget with different configs.
   */
  singletonId: z.string().optional()
})

/**
 * How media maps into the zone when its aspect differs.
 * Omitted = historical default (images `cover`, videos `contain`).
 */
export const sectionFitSchema = z.enum(['contain', 'cover'])

export const sectionSchema = z.object({
  x0: z.number(),
  y0: z.number(),
  width: z.number(),
  height: z.number(),
  fit: sectionFitSchema.optional()
})

export const layoutSchema = z.object({
  id: z.string(),
  name: z.string(),
  aspectRatio: aspectRatioSchema,
  width: z.number(),
  height: z.number(),
  sections: z.record(z.string(), sectionSchema)
})

/** `sequential` is accepted when parsing stored playlists and normalized to `rotating`. */
const embeddedPlaylistSelectionModeSchema = z
  .union([z.literal('random'), z.literal('rotating'), z.literal('sequential')])
  .transform((v): 'random' | 'rotating' => (v === 'sequential' ? 'rotating' : v))

export const playlistRefSchema = z.object({
  /** URN of the playlist to embed. Must belong to the same tenant. */
  playlistUrn: z.string(),
  /**
   * How many frames from the embedded playlist to show per loop.
   * null = play all active frames.
   */
  frameCount: z.number().int().min(1).nullable(),
  /** How frames are selected from the embedded playlist each loop. */
  selectionMode: embeddedPlaylistSelectionModeSchema.default('rotating'),
  /**
   * Legacy field kept for API payloads. Rotating playback always seeds the first window at index 0
   * ({@link selectEmbeddedFrames}); `random` ignores it.
   */
  offset: z.number().int().min(0).default(0)
})

export type TPlaylistRef = z.infer<typeof playlistRefSchema>

export type { TRssDirectoryRef } from './rssDirectory'
export { rssDirectoryRefSchema } from './rssDirectoryRef'

/** Drop legacy `rss_feed` facets from stored JSON so playlists still parse after RSS removal. */
function stripLegacyRssFacetsFromFrameRaw(input: unknown): unknown {
  if (input == null || typeof input !== 'object') {
    return input
  }
  const fr = input as Record<string, unknown>
  const facets = fr.facets
  if (!facets || typeof facets !== 'object') {
    return input
  }
  const nextFacets: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(facets as Record<string, unknown>)) {
    if (v && typeof v === 'object' && (v as { type?: unknown }).type === 'rss_feed') {
      continue
    }
    nextFacets[k] = v
  }
  return { ...fr, facets: nextFacets }
}

const frameBodySchema = z
  .object({
    id: z.string(),
    layout: layoutSchema,
    duration: z.number(),
    facets: z.record(z.string(), facetSchema),
    /**
     * Optional date-range start (ISO 8601 or date-only yyyy-MM-dd).
     * When set, the frame is only included in playback on or after this date (UTC, inclusive).
     */
    startDate: z.string().nullable().optional(),
    /**
     * Optional date-range end (ISO 8601 or date-only yyyy-MM-dd).
     * Exclusive: the frame is active when the reference instant is strictly before this boundary (UTC).
     */
    endDate: z.string().nullable().optional(),
    /**
     * Optional weekly recurring time windows. When non-empty, the frame is only included in playback
     * during at least one matching block (day-of-week + time-of-day, evaluated in UTC).
     * When empty or omitted, no time-of-day restriction applies (only the date range, if any).
     */
    timeBlocks: z.array(timeBlockSchema).optional(),
    /**
     * Optional geographic gate (center+radius circles; match is OR across circles).
     * Null / omitted = no geo gate on this frame.
     */
    geoFilter: playlistAssignmentGeoFilterSchema.nullable().optional(),
    /**
     * When {@link geoFilter} is set and player location is unknown, include this frame anyway.
     * Default false (hide when location unavailable).
     */
    showWhenLocationUnavailable: z.boolean().optional(),
    /**
     * When set, this frame is an embedded playlist frame: at playback time the player expands it
     * into the referenced playlist's frames (subset or all, per selectionMode/frameCount).
     * layout/duration/facets are vestigial placeholders when this field is present.
     */
    playlistRef: playlistRefSchema.optional(),
    /**
     * When set, this frame is a platform RSS directory frame: the player renders feed content
     * natively using the snapshotted layoutId/layoutOptions. facets are empty.
     */
    rssDirectoryRef: rssDirectoryRefSchema.optional(),
    /**
     * @deprecated Operator linking removed from the editor; stripped on playlist parse/save.
     * Playback may still inject this flag at resolve time (portal book / house embed).
     */
    playContinuousWithNext: z.boolean().optional(),
    /** Optional QR CTA overlays (tenant CTA catalog + layout coordinates). */
    ctas: z.array(ctaPlacementSchema).optional()
  })
  .superRefine((frame, ctx) => {
    if (frame.playlistRef || frame.rssDirectoryRef) { return }
    const result = validateFrameWidgetUniqueness(frame)
    if (!result.valid) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.message })
    }
  })

export const frameSchema = z.preprocess(stripLegacyRssFacetsFromFrameRaw, frameBodySchema)

/** Returns true when the frame is an embedded playlist frame (not a standard content frame). */
export function isEmbeddedPlaylistFrame(frame: TFrame): boolean {
  return frame.playlistRef != null
}

/** Returns true when the frame is a platform RSS directory frame. */
export function isRssDirectoryFrame(frame: TFrame): boolean {
  return frame.rssDirectoryRef != null
}

export const playlistSchema = z
  .object({
    title: z.string(),
    /** Legacy root key ignored on parse (orientation comes from each frame’s layout canvas). */
    aspectRatio: aspectRatioSchema.optional(),
    frames: z.array(frameSchema),
    metadata: z.record(z.string(), z.unknown()),
    /** Playlist-level widget config. Merged with facet and campaign (campaign wins). */
    widgetConfig: widgetConfigSchema,
    /**
     * How multiple playlists / frame sequences combine when this document drives scheduling.
     * Migrated from the `campaign` row (see docs/plans/playlist-architecture.md);
     * when omitted, registration / player code falls back to the parent campaign until campaigns are removed.
     */
    playbackMode: playbackModeCoercedSchema.optional(),
    /**
     * Legacy ordered frame slots (`urn|index`); schedule-root ordering uses {@link playbackMode} only (#360).
     */
    customSequence: z.array(z.string()).nullable().optional()
  })
  .transform((p) => {
    const { aspectRatio: _legacyAspectRatio, ...rest } = p
    const frames = stripPlayContinuousFromFrames(normalizeFrameContinuityFlags(rest.frames))
    return {
      ...rest,
      frames
    }
  })

export type TPlaylist = z.infer<typeof playlistSchema>
/** Wire / form payload before persistence; legacy root `aspectRatio` optional (stripped on parse). */
export type TPlaylistJsonInput = z.input<typeof playlistSchema>
/** SQLite `playlist_version.playlist` JSON (same as {@link TPlaylist}; legacy root `aspectRatio` tolerated on read). */
export type TPlaylistVersionDocument = TPlaylist
export type TFrame = z.infer<typeof frameSchema>
export type TLayout = z.infer<typeof layoutSchema>
export type TSection = z.infer<typeof sectionSchema>
export type TFacet = z.infer<typeof facetSchema>
